import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileCode, Copy, Check, Download, ExternalLink, ClipboardList, Rocket, Pencil } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractDraftDetail = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const originFrom = searchParams.get('from') || null;
  const originPortfolioId = searchParams.get('portfolio_id') || null;
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedInstructions, setCopiedInstructions] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deployForm, setDeployForm] = useState({ chain: '', contract_address: '', tx_hash: '' });
  const [deployEditing, setDeployEditing] = useState(false);
  const [deploySaving, setDeploySaving] = useState(false);
  const [deployError, setDeployError] = useState('');

  useEffect(() => {
    fetchDraft();
  }, [id]);

  const fetchDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [draftRes, exportRes] = await Promise.all([
        axios.get(`${API}/contracts/drafts/${id}`, { headers }),
        axios.get(`${API}/contracts/drafts/${id}/export`, { headers }).catch(() => null)
      ]);
      setDraft(draftRes.data.draft);
      if (exportRes?.data) setExportData(exportRes.data);
    } catch (err) {
      console.error('Error loading draft:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (draft?.generated_code) {
      navigator.clipboard.writeText(draft.generated_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadSol = () => {
    if (!exportData?.solidity_code) return;
    const blob = new Blob([exportData.solidity_code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportData.filename || 'Contract.sol';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyInstructions = () => {
    if (!exportData?.deploy_instructions_markdown) return;
    navigator.clipboard.writeText(exportData.deploy_instructions_markdown);
    setCopiedInstructions(true);
    setTimeout(() => setCopiedInstructions(false), 2000);
  };

  const handleOpenRemix = () => {
    window.open('https://remix.ethereum.org', '_blank', 'noopener');
  };

  const handleDeploySave = async () => {
    setDeployError('');
    if (!deployForm.chain) { setDeployError('Chain is required'); return; }
    if (!/^0x[0-9a-fA-F]{40}$/.test(deployForm.contract_address)) { setDeployError('Address must be 0x + 40 hex chars'); return; }
    if (deployForm.tx_hash && !/^0x[0-9a-fA-F]{32,}$/.test(deployForm.tx_hash)) { setDeployError('TX hash must be 0x + 32+ hex chars'); return; }
    setDeploySaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/contracts/drafts/${id}/deployment`, {
        deployment_status: 'DEPLOYED',
        chain: deployForm.chain,
        contract_address: deployForm.contract_address,
        tx_hash: deployForm.tx_hash || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchDraft();
      setDeployEditing(false);
    } catch (err) {
      setDeployError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setDeploySaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading draft...</div>;
  }
  if (!draft) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.status.error }}>Draft not found or access denied.</div>;
  }

  const STANDARD_COLORS = {
    ERC721: theme.colors.neon.blue,
    ERC1155: theme.colors.accent.teal,
    ERC20: theme.colors.accent.salmon,
  };
  const color = STANDARD_COLORS[draft.standard] || theme.colors.primary;

  const exportBtnBase = {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 18px',
    borderRadius: theme.radius.sm,
    fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
    border: 'none'
  };

  return (
    <div style={{ padding: '40px', maxWidth: '900px' }}>
      {(() => {
        // Batch 1C-B1 FINAL Part 5 — preserve safe origin context. Only
        // trust known internal `from` values.
        const KNOWN_ORIGINS = { edit: `/creator/portfolios/${originPortfolioId || ''}/edit?section=contract`, review: `/creator/portfolios/${originPortfolioId || ''}/review`, launch: `/creator/portfolios/${originPortfolioId || ''}/launch-readiness` };
        const backTarget = (originFrom && originPortfolioId && KNOWN_ORIGINS[originFrom]) || '/creator/contracts/templates';
        const backLabel = (originFrom === 'edit') ? 'Back to Edit Kickback'
          : (originFrom === 'review') ? 'Back to Kickback'
          : (originFrom === 'launch') ? 'Back to Launch Readiness'
          : 'Back to Templates';
        return (
          <button
            data-testid="back-to-origin"
            onClick={() => navigate(backTarget)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 0',
              background: 'none', border: 'none', color: theme.colors.text.secondary,
              fontSize: '14px', cursor: 'pointer', marginBottom: '24px'
            }}
          >
            <ArrowLeft size={16} /> {backLabel}
          </button>
        );
      })()}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px',
          background: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <FileCode size={22} style={{ color }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 data-testid="draft-detail-title" style={{
              fontFamily: theme.fonts.heading, fontSize: '24px', fontWeight: 700,
              color: theme.colors.text.primary
            }}>
              {draft.params?.contract_name || draft.template_name || 'Untitled Draft'}
            </h1>
            <span style={{
              padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
              background: draft.status === 'READY' ? `${theme.colors.status.success}15` : `${theme.colors.status.warning}15`,
              color: draft.status === 'READY' ? theme.colors.status.success : theme.colors.status.warning
            }}>
              {draft.status}
            </span>
          </div>
          <p style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
            {draft.standard} &middot; {draft.template_name} &middot; Created {new Date(draft.created_at).toLocaleString()}
          </p>
        </div>
        {/* Batch 1C-B1 FINAL Part 6 — Contract Workspace obeys canonical
            deployment gating. Deploy Contract is only rendered when the
            draft is technically eligible; otherwise show Prepare Contract
            (disabled visual, routes to configure) or View Deployment. */}
        {(() => {
          const isDeployed = !!(draft.deployed || draft.deployment_status === 'DEPLOYED');
          const hasCode = !!draft.generated_code;
          if (isDeployed) {
            return (
              <button data-testid="workspace-view-deployment-btn"
                onClick={() => navigate(`/creator/contracts/${id}/deploy`)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.4)' }}>
                <Rocket size={16} /> View Deployment
              </button>
            );
          }
          if (!hasCode) {
            return (
              <button data-testid="workspace-prepare-contract-btn"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const r = await fetch(`${API}/contracts/drafts/${id}/prepare`, {
                      method: 'POST', headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!r.ok) throw new Error('prepare_failed');
                    const j = await r.json();
                    setDraft(j.draft || draft);
                  } catch (_) {
                    setDeployError('Contract preparation failed. Please verify the configuration and try again.');
                  }
                }}
                title="Generate Solidity code for this draft"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', background: 'linear-gradient(135deg, #F59E0B, #F97316)', color: '#FFF', border: 'none' }}>
                <Rocket size={16} /> Prepare Contract
              </button>
            );
          }
          return (
            <button data-testid="workspace-deploy-btn"
              onClick={() => navigate(`/creator/contracts/${id}/deploy`)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', background: 'linear-gradient(135deg, #10B981, #06B6D4)', color: '#FFF', border: 'none' }}>
              <Rocket size={16} /> Deploy Contract
            </button>
          );
        })()}
      </div>

      {/* P9.1: Deployed info banner */}
      {draft.deployed && (draft.deployed_address || draft.contract_address) && (
        <div data-testid="deployed-info-banner" style={{
          padding: '16px 20px', borderRadius: '12px', marginBottom: '20px',
          background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.25)',
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#10B981' }}>Deployed</span>
          <code style={{ fontSize: '13px', color: theme.colors.text.primary, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {draft.deployed_address || draft.contract_address}
          </code>
          <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>
            on {draft.deployed_chain || draft.chain || 'unknown'}
          </span>
        </div>
      )}

      {/* P6.1A: Export Actions */}
      {exportData && (
        <div data-testid="export-actions" style={{
          background: theme.colors.bg.card,
          border: `1px solid ${theme.colors.borders}`,
          borderRadius: theme.radius.card,
          padding: '20px 24px',
          marginBottom: '20px',
          boxShadow: theme.colors.shadows.sm,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, marginRight: '4px' }}>
            Export:
          </span>
          <button
            data-testid="download-sol-btn"
            onClick={handleDownloadSol}
            style={{
              ...exportBtnBase,
              background: theme.colors.primary,
              color: '#FFFFFF'
            }}
          >
            <Download size={15} /> Download .sol
          </button>
          <button
            data-testid="copy-instructions-btn"
            onClick={handleCopyInstructions}
            style={{
              ...exportBtnBase,
              background: copiedInstructions ? `${theme.colors.status.success}12` : theme.colors.bg.secondary,
              color: copiedInstructions ? theme.colors.status.success : theme.colors.text.primary,
              border: `1px solid ${copiedInstructions ? theme.colors.status.success : theme.colors.borders}`
            }}
          >
            {copiedInstructions ? <><Check size={15} /> Copied</> : <><ClipboardList size={15} /> Copy Deploy Instructions</>}
          </button>
          <button
            data-testid="open-remix-btn"
            onClick={handleOpenRemix}
            style={{
              ...exportBtnBase,
              background: theme.colors.bg.secondary,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.borders}`
            }}
          >
            <ExternalLink size={15} /> Open Remix (guide)
          </button>
        </div>
      )}

      {/* P6.3: Deployment Section */}
      <div data-testid="deployment-section" style={{
        background: theme.colors.bg.card,
        border: `1px solid ${theme.colors.borders}`,
        borderRadius: theme.radius.card,
        padding: '24px',
        marginBottom: '20px',
        boxShadow: theme.colors.shadows.sm
      }}>
        {/* Batch 1C-B1 FINAL PRODUCTION CLOSURE — hide the deployment
            record form entirely when the contract is neither deployed
            nor has generated code yet. Contract Address / TX Hash are
            deployment-time artifacts and MUST NOT block Prepare
            Contract or be shown as prerequisites. */}
        {(!draft.deployment_status || draft.deployment_status !== 'DEPLOYED') && !draft.generated_code ? (
          <div data-testid="deployment-locked-notice" style={{ padding: 12, background: theme.colors.bg.secondary, border: `1px dashed ${theme.colors.borders}`, borderRadius: theme.radius.sm, fontSize: 13, color: theme.colors.text.muted }}>
            <strong>Deployment</strong> — Prepare the contract first. A Contract Address is issued by the blockchain after deployment; it is not required to prepare the Solidity code.
          </div>
        ) : (
        <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Rocket size={18} style={{ color: draft.deployment_status === 'DEPLOYED' ? theme.colors.status.success : theme.colors.text.muted }} />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>Deployment</h2>
            {draft.deployment_status === 'DEPLOYED' && (
              <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: `${theme.colors.status.success}15`, color: theme.colors.status.success }}>
                Deployed
              </span>
            )}
          </div>
          {draft.deployment_status === 'DEPLOYED' && !deployEditing && (
            <button data-testid="deploy-edit-btn" onClick={() => {
              setDeployForm({ chain: draft.chain || '', contract_address: draft.contract_address || '', tx_hash: draft.tx_hash || '' });
              setDeployEditing(true);
            }} style={{
              display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
              background: 'transparent', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
              fontSize: '12px', color: theme.colors.text.secondary, cursor: 'pointer'
            }}>
              <Pencil size={13} /> Edit
            </button>
          )}
        </div>

        {draft.deployment_status === 'DEPLOYED' && !deployEditing ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            <div style={{ padding: '10px 14px', background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}` }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Chain</div>
              <div data-testid="deploy-chain" style={{ fontSize: '14px', color: theme.colors.text.primary, fontWeight: 500 }}>{draft.chain}</div>
            </div>
            <div style={{ padding: '10px 14px', background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}` }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Contract Address</div>
              <div data-testid="deploy-address" style={{ fontSize: '13px', color: theme.colors.text.primary, fontWeight: 500, wordBreak: 'break-all', fontFamily: "'JetBrains Mono', monospace" }}>{draft.contract_address}</div>
            </div>
            {draft.tx_hash && (
              <div style={{ padding: '10px 14px', background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}` }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>TX Hash</div>
                <div data-testid="deploy-tx" style={{ fontSize: '13px', color: theme.colors.text.primary, fontWeight: 500, wordBreak: 'break-all', fontFamily: "'JetBrains Mono', monospace" }}>{draft.tx_hash}</div>
              </div>
            )}
            {draft.deployed_at && (
              <div style={{ padding: '10px 14px', background: theme.colors.bg.secondary, borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}` }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Deployed At</div>
                <div style={{ fontSize: '14px', color: theme.colors.text.primary, fontWeight: 500 }}>{new Date(draft.deployed_at).toLocaleString()}</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {deployError && <p data-testid="deploy-error" style={{ fontSize: '13px', color: theme.colors.status.error, margin: 0 }}>{deployError}</p>}
            <div>
              <label style={{ fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary, display: 'block', marginBottom: '6px' }}>Chain *</label>
              <select data-testid="deploy-chain-select" value={deployForm.chain} onChange={(e) => setDeployForm(f => ({ ...f, chain: e.target.value }))} style={{
                width: '100%', padding: '10px 12px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
                fontSize: '14px', background: theme.colors.bg.card, color: theme.colors.text.primary
              }}>
                <option value="">Select chain...</option>
                <option value="ethereum">Ethereum Mainnet</option>
                <option value="polygon">Polygon</option>
                <option value="base">Base</option>
                <option value="arbitrum">Arbitrum</option>
                <option value="optimism">Optimism</option>
                <option value="bsc">BSC (BNB Chain)</option>
                <option value="testnet-sepolia">Testnet — Sepolia</option>
                <option value="testnet-goerli">Testnet — Goerli</option>
                <option value="testnet-mumbai">Testnet — Mumbai</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary, display: 'block', marginBottom: '6px' }}>Contract Address *</label>
              <input data-testid="deploy-address-input" type="text" value={deployForm.contract_address}
                onChange={(e) => setDeployForm(f => ({ ...f, contract_address: e.target.value }))}
                placeholder="0x0000000000000000000000000000000000000000"
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, fontSize: '14px', background: theme.colors.bg.card, color: theme.colors.text.primary, fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary, display: 'block', marginBottom: '6px' }}>TX Hash (optional)</label>
              <input data-testid="deploy-tx-input" type="text" value={deployForm.tx_hash}
                onChange={(e) => setDeployForm(f => ({ ...f, tx_hash: e.target.value }))}
                placeholder="0x..."
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, fontSize: '14px', background: theme.colors.bg.card, color: theme.colors.text.primary, fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button data-testid="deploy-save-btn" onClick={handleDeploySave} disabled={deploySaving} style={{
                padding: '10px 20px', background: theme.colors.primary, color: '#FFFFFF', border: 'none',
                borderRadius: theme.radius.sm, fontSize: '14px', fontWeight: 600, cursor: deploySaving ? 'not-allowed' : 'pointer', opacity: deploySaving ? 0.7 : 1
              }}>
                {deploySaving ? 'Saving...' : 'Save Deployment'}
              </button>
              {deployEditing && (
                <button onClick={() => setDeployEditing(false)} style={{
                  padding: '10px 20px', background: 'transparent', color: theme.colors.text.secondary,
                  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, fontSize: '14px', cursor: 'pointer'
                }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Params */}
      <div style={{
        background: theme.colors.bg.card,
        border: `1px solid ${theme.colors.borders}`,
        borderRadius: theme.radius.card,
        padding: '24px',
        marginBottom: '20px',
        boxShadow: theme.colors.shadows.sm
      }}>
        <h2 style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '16px' }}>
          Parameters
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
          {Object.entries(draft.params || {}).map(([key, val]) => (
            <div key={key} data-testid={`param-${key}`} style={{
              padding: '10px 14px',
              background: theme.colors.bg.secondary,
              borderRadius: theme.radius.sm,
              border: `1px solid ${theme.colors.borders}`
            }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                {key.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.text.primary, fontWeight: 500, wordBreak: 'break-all' }}>
                {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val || '\u2014')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Generated Code */}
      {draft.generated_code && (
        <div style={{
          background: theme.colors.bg.card,
          border: `1px solid ${theme.colors.borders}`,
          borderRadius: theme.radius.card,
          overflow: 'hidden',
          boxShadow: theme.colors.shadows.sm,
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 20px',
            borderBottom: `1px solid ${theme.colors.borders}`,
            background: theme.colors.bg.secondary
          }}>
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>
              Generated Solidity Code
            </h2>
            <button
              data-testid="copy-code-btn"
              onClick={handleCopyCode}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px',
                background: copied ? `${theme.colors.status.success}15` : 'transparent',
                border: `1px solid ${copied ? theme.colors.status.success : theme.colors.borders}`,
                borderRadius: theme.radius.sm,
                fontSize: '13px', fontWeight: 500,
                color: copied ? theme.colors.status.success : theme.colors.text.secondary,
                cursor: 'pointer'
              }}
            >
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </button>
          </div>
          <pre data-testid="generated-code" style={{
            padding: '20px',
            margin: 0,
            fontSize: '13px',
            lineHeight: '1.6',
            color: theme.colors.text.primary,
            background: '#FAFBFC',
            overflowX: 'auto',
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
            whiteSpace: 'pre',
            maxHeight: '600px',
            overflowY: 'auto'
          }}>
            {draft.generated_code}
          </pre>
        </div>
      )}

      {/* Deploy Instructions */}
      {exportData?.deploy_instructions_markdown && (
        <div data-testid="deploy-instructions" style={{
          background: theme.colors.bg.card,
          border: `1px solid ${theme.colors.borders}`,
          borderRadius: theme.radius.card,
          overflow: 'hidden',
          boxShadow: theme.colors.shadows.sm
        }}>
          <div style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${theme.colors.borders}`,
            background: theme.colors.bg.secondary
          }}>
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>
              Deploy Instructions
            </h2>
          </div>
          <pre style={{
            padding: '20px',
            margin: 0,
            fontSize: '13px',
            lineHeight: '1.7',
            color: theme.colors.text.primary,
            background: '#FAFBFC',
            overflowX: 'auto',
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
          }}>
            {exportData.deploy_instructions_markdown}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ContractDraftDetail;
